import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { useSwipeGesture } from "@/hooks/useSwipeGesture";

interface CollapsibleSectionProps {
  title: string;
  icon?: React.ComponentType<{ className?: string }>;
  defaultExpanded?: boolean;
  children: React.ReactNode;
  className?: string;
  badge?: React.ReactNode;
}

const CollapsibleSection = ({
  title,
  icon: Icon,
  defaultExpanded = true,
  children,
  className,
  badge
}: CollapsibleSectionProps) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  // Swipe gesture support - swipe left to collapse, right to expand
  const { handlers, swipeDirection } = useSwipeGesture({
    onSwipeLeft: () => setIsExpanded(false),
    onSwipeRight: () => setIsExpanded(true),
    threshold: 60
  });

  return (
    <motion.div 
      className={cn("border rounded-lg overflow-hidden", className)}
      {...handlers}
      animate={{
        x: swipeDirection === 'left' ? -5 : swipeDirection === 'right' ? 5 : 0,
        opacity: swipeDirection ? 0.9 : 1
      }}
      transition={{ duration: 0.1 }}
    >
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-3 bg-muted/30 hover:bg-muted/50 transition-colors touch-manipulation"
      >
        <div className="flex items-center gap-2">
          {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
          <span className="font-medium text-sm">{title}</span>
          {badge}
        </div>
        <motion.div
          animate={{ rotate: isExpanded ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        </motion.div>
      </button>
      
      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
          >
            <div className="p-3">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default CollapsibleSection;
